import React, { useMemo, useState } from 'react';
import { Button, Skeleton } from 'antd';
import { Link } from 'react-router-dom';

import { RecordTitle } from '../common/RecordBadge';
import { cardOwnerOf } from '../cards/CardVisuals';
import { CHEVRON_DOWN, CHEVRON_RIGHT, LOCK } from '../icons';
import { getCardScope } from '../../utils/cardScopes';
import { formatCount, joinMeta } from '../../utils/counts';
import { formatDisplayDateTime, formatShortDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { cardPagePath } from '../../utils/cards';
import { compareByName } from '../../utils/sort';

/**
 * The collection home's cards, compact: one row per card — its plain title
 * (chip + name, the door to the list filtered to it), an activity line, and
 * a chevron that opens the row's detail in place. The list leads with the
 * cards in use (most items lately, then the newest item, then the most
 * items, then name) and shows `limit` rows before a "Show all" door, so a
 * collection with thirty cards spends the home on the five that matter.
 *
 * `stats` is the per-card activity from GET /api/items/counts, keyed by
 * card id — `{ count, recentCount, lastCreatedAt, lastUpdatedAt }` — or null
 * when the API has none (the rows then read name-ordered and without a
 * number: the platform's counts rule, zero is not information).
 */

export const DEFAULT_CARD_LIST_LIMIT = 6;

const toTime = value => {
	const t = value ? new Date(value).getTime() : NaN;
	return Number.isFinite(t) ? t : 0;
};

/**
 * Activity order: cards used lately first. Ties fall back to the newest
 * item, then the most items, then the name — so an idle collection still
 * reads alphabetically.
 */
export function sortCardsByActivity(cards, stats) {
	const list = Array.isArray(cards) ? [...cards] : [];
	const statOf = card => (stats && stats[normalizeId(card?._id)]) || null;
	return list.sort((a, b) => {
		const sa = statOf(a);
		const sb = statOf(b);
		return (
			((sb?.recentCount || 0) - (sa?.recentCount || 0))
			|| (toTime(sb?.lastCreatedAt) - toTime(sa?.lastCreatedAt))
			|| ((sb?.count || 0) - (sa?.count || 0))
			|| compareByName(a, b)
		);
	});
}

/** "12 items · 4 in 30 days · last added Sep 14" — blanks drop out. */
export function describeCardActivity(stat, { windowDays = 30 } = {}) {
	if (!stat) return '';
	return joinMeta(
		formatCount(stat.count, 'item'),
		stat.recentCount > 0 ? `${stat.recentCount} in ${windowDays} days` : null,
		stat.lastCreatedAt ? `last added ${formatShortDate(stat.lastCreatedAt)}` : null,
	);
}

const CardRow = ({ card, stat, childCount, elementCount, itemsPath, collectionId, canManage, expanded, onToggle, windowDays }) => {
	const id = normalizeId(card?._id);
	const listPath = `${itemsPath}?card=${id}`;
	const activity = describeCardActivity(stat, { windowDays });
	const description = typeof card?.description === 'string' ? card.description.trim() : '';
	const Chevron = expanded ? CHEVRON_DOWN : CHEVRON_RIGHT;
	const name = card?.name || 'Untitled card';
	const detailId = `collection-card-${id}`;

	return (
		<li className={['collection-card-row', expanded ? 'collection-card-row--open' : ''].filter(Boolean).join(' ')} data-card-id={id}>
			<div className="collection-card-row__line">
				<Button
					type="text"
					size="small"
					className="collection-card-row__toggle"
					icon={<Chevron />}
					aria-label={expanded ? `Hide details for ${name}` : `Show details for ${name}`}
					aria-expanded={expanded}
					aria-controls={detailId}
					onClick={onToggle}
				/>
				<RecordTitle
					kind="card"
					record={card}
					title={name}
					size="sm"
					to={listPath}
					className="collection-card-row__title"
					// A parent's home lists its sub-collections' cards too, and a
					// sub-collection's its parent's: each says where it lives.
					collection={cardOwnerOf(card)}
				/>
				{getCardScope(card) === 'global' ? (
					<span className="collection-card-row__lock" role="img" aria-label="Global card" title="Global card">
						<LOCK />
					</span>
				) : null}
				{activity ? (
					<span className="collection-card-row__activity meta-line" data-testid="card-activity">{activity}</span>
				) : null}
			</div>
			{expanded ? (
				<div className="collection-card-row__detail" id={detailId}>
					{description ? <p className="collection-card-row__description">{description}</p> : null}
					<div className="collection-card-row__facts meta-line">
						{joinMeta(
							formatCount(elementCount, 'element'),
							formatCount(childCount, 'child card'),
							stat?.lastUpdatedAt ? `last updated ${formatDisplayDateTime(stat.lastUpdatedAt)}` : null,
						) || 'No elements yet'}
					</div>
					<div className="collection-card-row__doors">
						<Link to={listPath}>Open items</Link>
						{canManage ? <Link to={cardPagePath(card?._id ? card : id, collectionId)}>Card settings</Link> : null}
					</div>
				</div>
			) : null}
		</li>
	);
};

export default function CollectionCardList({
	cards = [],
	stats = null,
	windowDays = 30,
	limit = DEFAULT_CARD_LIST_LIMIT,
	itemsPath,
	// The collection these cards are listed in: "Card settings" carries it
	// to the card page, so its tile layout can be arranged for it.
	collectionId = null,
	// Elements per card including the inherited ones (the definitions
	// context resolver), for the detail's element count.
	getElementsForCard,
	canManage = false,
	loading = false,
	emptyMessage = 'No cards in this collection yet.',
}) {
	const [showAll, setShowAll] = useState(false);
	const [openId, setOpenId] = useState(null);

	const ordered = useMemo(() => sortCardsByActivity(cards, stats), [cards, stats]);
	const childCounts = useMemo(() => {
		const counts = new Map();
		(cards || []).forEach(card => {
			const parentId = normalizeId(card?.parentCardId);
			if (parentId) counts.set(parentId, (counts.get(parentId) || 0) + 1);
		});
		return counts;
	}, [cards]);

	if (loading && !ordered.length) {
		return (
			<ul className="collection-card-list" aria-busy="true">
				{Array.from({ length: 3 }).map((_, index) => (
					<li key={index} className="collection-card-row collection-card-row--skeleton">
						<Skeleton.Avatar active size={27} shape="square" />
						<Skeleton.Input active size="small" style={{ width: 160 }} />
					</li>
				))}
			</ul>
		);
	}
	if (!ordered.length) {
		return <div className="collection-card-list__empty">{emptyMessage}</div>;
	}

	const cap = Math.max(1, Number.isFinite(limit) ? limit : DEFAULT_CARD_LIST_LIMIT);
	const visible = showAll ? ordered : ordered.slice(0, cap);

	return (
		<>
			<ul className="collection-card-list">
				{visible.map(card => {
					const id = normalizeId(card?._id);
					return (
						<CardRow
							key={id}
							card={card}
							stat={stats ? stats[id] : null}
							childCount={childCounts.get(id) || 0}
							elementCount={typeof getElementsForCard === 'function'
								? (getElementsForCard(id) || []).length
								: (card?.elements || []).length}
							itemsPath={itemsPath}
							collectionId={collectionId}
							canManage={canManage}
							expanded={openId === id}
							onToggle={() => setOpenId(current => (current === id ? null : id))}
							windowDays={windowDays}
						/>
					);
				})}
			</ul>
			{ordered.length > cap ? (
				<Button
					type="link"
					size="small"
					className="collection-card-list__more"
					onClick={() => setShowAll(value => !value)}
					aria-expanded={showAll}
				>
					{showAll ? 'Show fewer' : `Show all ${formatCount(ordered.length, 'card')}`}
				</Button>
			) : null}
		</>
	);
}
